// index_page.ts — landing-page builds list.
//
// Reads the localStorage build index of EVERY game namespace (each
// planner page persists under `${game}-planner:` — see wizard_chrome's
// STORE_BASE), renders the merged list (with ascendancy/class portrait
// art from each game's build_meta.json), handles delete-this-build
// clicks, and re-renders when another tab mutates localStorage.
// Loaded as a classic <script src=> tag from index.html.

import type {
  AnyPersistedPlan, Plan, PlanIndexEntry, PlanV3,
} from "../../types/shared.d.ts";
import {
  GAME_DEFINITIONS, gameDefinitionForPlan,
} from "../../crates/tree_render/assets/planner/game_profile.ts";
import {
  migratePlanV2ToV3, projectPlanV3ToV2, validatePlanV3,
} from "./plan_v3.ts";
import {
  legacyPlanBackupKey, pendingPlanKey, saveNativePlan,
} from "./plan_storage.ts";

// One row per game the site ships. Adding a game = adding a row; the
// storage prefix and planner URL are the only per-game facts here.
interface GameStore {
  id: string;
  label: string;           // badge on the card ('' = no badge, the default game)
  base: string;            // localStorage namespace
  planner: string;         // planner page URL
  metaUrl: string;         // build_meta.json with the portraits map
}
const GAMES: GameStore[] = [
  GAME_DEFINITIONS.poe2,
  GAME_DEFINITIONS.poe1,
].map(game => ({
  id: game.id,
  label: game.shortLabel,
  base: game.storageNamespace,
  planner: game.plannerPath,
  metaUrl: game.assets.buildMeta,
}));
const keyIndex  = (g: GameStore): string => `${g.base}:index`;
const keyPrefix = (g: GameStore): string => `${g.base}:plan:`;

interface IndexedBuild extends PlanIndexEntry { game: GameStore }
function load(): IndexedBuild[] {
  const all: IndexedBuild[] = [];
  for (const g of GAMES) {
    try {
      const idx: PlanIndexEntry[] = JSON.parse(localStorage.getItem(keyIndex(g)) || "[]");
      for (const b of idx) all.push({ ...b, game: g });
    } catch (e) { /* corrupt index → skip this game's list */ }
  }
  return all;
}
function fmtDate(iso: string | undefined): string {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); }
  catch { return iso; }
}
function escHtml(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, c => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c
  ));
}

// Ascendancy/class portrait art (first-party, emitted by tree_render
// into each game's build_meta.json). Loads once per game; cards render
// text-only until it lands, then re-render with art.
const portraitsByGame: Record<string, Record<string, string>> = {};
for (const g of GAMES) {
  fetch(g.metaUrl, { cache: "force-cache" })
    .then(r => (r.ok ? r.json() : null))
    .then((d: { portraits?: Record<string, string> } | null) => {
      if (d?.portraits) { portraitsByGame[g.id] = d.portraits; render(); }
    })
    .catch(() => { /* text-only cards */ });
}

function portraitFor(b: IndexedBuild): string {
  const portraits = portraitsByGame[b.game.id] ?? {};
  // Ascendancy portrait first (the iconic art), class portrait as the
  // pre-ascension fallback.
  const key = (b.ascendancy && portraits[b.ascendancy]) ? b.ascendancy
    : (b.class && portraits[b.class]) ? b.class
    : null;
  if (!key) return '<span class="portrait blank"></span>';
  return '<span class="portrait"><img src="' + escHtml(portraits[key]!) + '" alt="" loading="lazy"></span>';
}

// Game filter ("all" | game id). Session-scoped on purpose — a
// returning user starts at the full list.
let gameFilter = "all";
const filterEl = document.getElementById("game-filter");
if (filterEl) {
  filterEl.addEventListener("click", e => {
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button[data-game]");
    if (!btn) return;
    gameFilter = btn.dataset.game || "all";
    filterEl.querySelectorAll("button").forEach(b =>
      b.classList.toggle("active", b === btn));
    render();
  });
}

function render(): void {
  const all = load().sort((a, b) => (b.savedAt || "").localeCompare(a.savedAt || ""));
  const idx = gameFilter === "all" ? all : all.filter(b => b.game.id === gameFilter);
  const ul = document.getElementById("build-list");
  if (!ul) return;
  if (idx.length === 0) {
    const g = GAMES.find(x => x.id === gameFilter);
    ul.innerHTML = g
      ? `<li class="empty">No ${escHtml(g.label)} builds yet — <a href="${g.planner}?new=1">start one</a>.</li>`
      : '<li class="empty">No saved builds yet. Start one above.</li>';
    return;
  }
  ul.innerHTML = idx.map(b => `
    <li>
      ${portraitFor(b)}
      <div class="build-main">
        <a class="name" href="${b.game.planner}?build=${encodeURIComponent(b.id)}">${escHtml(b.name || "(untitled)")}</a>
        <span class="meta">${escHtml(b.class || "—")}${b.ascendancy ? " · " + escHtml(b.ascendancy) : ""} · <b>${b.nodeCount || 0}</b> nodes · ${escHtml(fmtDate(b.savedAt))}</span>
      </div>
      <span class="game-badge ${escHtml(b.game.id)}">${escHtml(b.game.label)}</span>
      <button class="dl" data-id="${escHtml(b.id)}" data-game="${escHtml(b.game.id)}" title="Download this build as a JSON backup">⬇</button>
      <button class="rm" data-id="${escHtml(b.id)}" data-game="${escHtml(b.game.id)}" title="Delete this build">✕</button>
    </li>
  `).join("");
}

// ---- Backup: per-card download + landing-page import -----------------
// The exported file is the persisted plan verbatim (lossless
// poe2-planner-plan v2 — the planner normalizes on open, so import
// stays a shallow store-and-index here; no logic duplicated from
// build_io/wizard_chrome). The game is derived from the plan's
// game-namespaced patch ("poe1.*" → poe1), matching wizard_chrome's
// patch rules.
function gameOfPlan(plan: { game?: string; patch?: string | null }): GameStore {
  const definition = gameDefinitionForPlan(plan);
  return GAMES.find(g => g.id === definition.id)!;
}
function downloadBuild(g: GameStore, id: string): void {
  const raw = localStorage.getItem(keyPrefix(g) + id);
  if (!raw) { alert("Build data not found in this browser."); return; }
  let name = "build";
  try {
    const plan = JSON.parse(raw) as AnyPersistedPlan;
    const title = plan.version === 3 ? plan.identity.name : plan.name;
    name = (title || "build").replace(/[^\w.-]+/g, "_");
  } catch (e) { /* keep default */ }
  const blob = new Blob([raw], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.${g.id}.buildwright.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}
const importBtn = document.getElementById("import-build-btn");
const importFile = document.getElementById("import-build-file") as HTMLInputElement | null;
if (importBtn && importFile) {
  importBtn.addEventListener("click", () => importFile.click());
  importFile.addEventListener("change", () => {
    const f = importFile.files?.[0];
    importFile.value = "";
    if (!f) return;
    void f.text().then(text => {
      let decoded: AnyPersistedPlan;
      try { decoded = JSON.parse(text) as AnyPersistedPlan; } catch (e) { alert("Not a JSON file."); return; }
      if (!(["buildwright-planner-plan", "poe2-planner-plan"].includes(decoded.format || "")) ||
          (decoded.version !== 2 && decoded.version !== 3)) {
        alert("Not a Buildwright planner backup. GGG .build files import inside the PoE2 planner instead.");
        return;
      }
      const g = gameOfPlan(decoded);
      let nativePlan: PlanV3;
      if (decoded.version === 2) {
        nativePlan = migratePlanV2ToV3(decoded as Plan);
      } else {
        nativePlan = structuredClone(decoded as PlanV3);
        const errors = validatePlanV3(nativePlan);
        if (errors.length) {
          alert("Invalid Buildwright state graph: " + errors.join("; "));
          return;
        }
      }
      // Keep the original id unless it already exists here — never
      // silently overwrite a stored build on import.
      let id = typeof nativePlan.id === "string" && nativePlan.id
        ? nativePlan.id
        : Math.random().toString(36).slice(2, 10);
      if (localStorage.getItem(keyPrefix(g) + id)) {
        id = Math.random().toString(36).slice(2, 10);
      }
      nativePlan.id = id;
      const saved = saveNativePlan(
        localStorage,
        keyPrefix(g) + id,
        nativePlan,
        g.id as "poe1" | "poe2",
      ).plan;
      const plan = projectPlanV3ToV2(saved);
      const last = plan.captures[plan.captures.length - 1];
      const entry: PlanIndexEntry = {
        id, name: plan.name || "(untitled)", savedAt: saved.savedAt!,
        class: plan.class ?? null, ascendancy: last?.ascendancy ?? null,
        nodeCount: last?.passives?.length ?? 0, captureCount: saved.states.length,
      };
      try {
        const idx: PlanIndexEntry[] = JSON.parse(localStorage.getItem(keyIndex(g)) || "[]");
        localStorage.setItem(keyIndex(g), JSON.stringify([entry, ...idx.filter(b => b.id !== id)]));
      } catch (e) {
        localStorage.setItem(keyIndex(g), JSON.stringify([entry]));
      }
      render();
    });
  });
}

const buildList = document.getElementById("build-list");
if (buildList) {
  buildList.addEventListener("click", e => {
    const dl = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button.dl");
    if (dl) {
      const g = GAMES.find(x => x.id === dl.dataset.game);
      if (g && dl.dataset.id) downloadBuild(g, dl.dataset.id);
      return;
    }
    const btn = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>("button.rm");
    if (!btn) return;
    const id = btn.dataset.id;
    const g = GAMES.find(x => x.id === btn.dataset.game);
    if (!id || !g) return;
    if (!confirm("Delete this build? This cannot be undone.")) return;
    const primary = keyPrefix(g) + id;
    localStorage.removeItem(primary);
    localStorage.removeItem(pendingPlanKey(primary));
    localStorage.removeItem(legacyPlanBackupKey(primary));
    try {
      const idx: PlanIndexEntry[] = JSON.parse(localStorage.getItem(keyIndex(g)) || "[]");
      localStorage.setItem(keyIndex(g), JSON.stringify(idx.filter(b => b.id !== id)));
    } catch (e) { /* corrupt index → nothing to rewrite */ }
    render();
  });
}

render();
// Re-render if localStorage changes in another tab.
window.addEventListener("storage", e => {
  if (!e.key) return;
  for (const g of GAMES) {
    if (e.key === keyIndex(g) || e.key.startsWith(keyPrefix(g))) { render(); return; }
  }
});
